import { useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type {
  FeatureFlagAssignmentTarget,
  FeatureFlagClientConfigurationMap,
  FeatureFlagFormFieldErrors,
  FeatureFlagTargetClient,
} from '../../../types/features/featureFlags';

type UseFeatureFlagAssignmentControlsParams = {
  activeTargetClient: FeatureFlagTargetClient;
  setClientConfigurations: Dispatch<SetStateAction<FeatureFlagClientConfigurationMap>>;
  setFeatureFieldErrors: Dispatch<SetStateAction<FeatureFlagFormFieldErrors>>;
  setFeatureFormError: Dispatch<SetStateAction<string>>;
  setStoreAssignmentsChanged: Dispatch<SetStateAction<boolean>>;
  setUserAssignmentsChanged: Dispatch<SetStateAction<boolean>>;
};

export const useFeatureFlagAssignmentControls = ({
  activeTargetClient,
  setClientConfigurations,
  setFeatureFieldErrors,
  setFeatureFormError,
  setStoreAssignmentsChanged,
  setUserAssignmentsChanged,
}: UseFeatureFlagAssignmentControlsParams) => {
  const handleStoreAssignmentChange = useCallback((storeId: string, assigned: boolean) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      storeIds: undefined,
      userIds: undefined,
    }));
    setStoreAssignmentsChanged(true);
    setClientConfigurations((currentConfigurations) => {
      const currentStoreIds = currentConfigurations[activeTargetClient].storeIds;
      const nextStoreIds = assigned
        ? Array.from(new Set([...currentStoreIds, storeId]))
        : currentStoreIds.filter((currentStoreId) => currentStoreId !== storeId);

      return {
        ...currentConfigurations,
        [activeTargetClient]: {
          ...currentConfigurations[activeTargetClient],
          storeIds: nextStoreIds,
        },
      };
    });
  }, [
    activeTargetClient,
    setClientConfigurations,
    setFeatureFieldErrors,
    setFeatureFormError,
    setStoreAssignmentsChanged,
  ]);

  const handleBulkStoreAssignmentChange = useCallback((storeIds: string[], assigned: boolean) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      storeIds: undefined,
      userIds: undefined,
    }));
    setStoreAssignmentsChanged(true);
    setClientConfigurations((currentConfigurations) => {
      const currentStoreIds = currentConfigurations[activeTargetClient].storeIds;
      const bulkStoreIdSet = new Set(storeIds);
      const nextStoreIds = assigned
        ? Array.from(new Set([...currentStoreIds, ...storeIds]))
        : currentStoreIds.filter((currentStoreId) => !bulkStoreIdSet.has(currentStoreId));

      return {
        ...currentConfigurations,
        [activeTargetClient]: {
          ...currentConfigurations[activeTargetClient],
          storeIds: nextStoreIds,
        },
      };
    });
  }, [
    activeTargetClient,
    setClientConfigurations,
    setFeatureFieldErrors,
    setFeatureFormError,
    setStoreAssignmentsChanged,
  ]);

  const handleAssignmentTargetChange = useCallback((target: FeatureFlagAssignmentTarget) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      storeIds: undefined,
      userIds: undefined,
    }));
    setClientConfigurations((currentConfigurations) => ({
      ...currentConfigurations,
      [activeTargetClient]: {
        ...currentConfigurations[activeTargetClient],
        assignmentTarget: target,
      },
    }));
  }, [activeTargetClient, setClientConfigurations, setFeatureFieldErrors, setFeatureFormError]);

  const handleUserAssignmentChange = useCallback((userId: string, assigned: boolean) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      userIds: undefined,
    }));
    setUserAssignmentsChanged(true);
    setClientConfigurations((currentConfigurations) => {
      const currentUserIds = currentConfigurations[activeTargetClient].userIds;
      const nextUserIds = assigned
        ? Array.from(new Set([...currentUserIds, userId]))
        : currentUserIds.filter((currentUserId) => currentUserId !== userId);

      return {
        ...currentConfigurations,
        [activeTargetClient]: {
          ...currentConfigurations[activeTargetClient],
          userIds: nextUserIds,
        },
      };
    });
  }, [
    activeTargetClient,
    setClientConfigurations,
    setFeatureFieldErrors,
    setFeatureFormError,
    setUserAssignmentsChanged,
  ]);

  const handleBulkUserAssignmentChange = useCallback((userIds: string[], assigned: boolean) => {
    setFeatureFormError('');
    setFeatureFieldErrors((currentErrors) => ({
      ...currentErrors,
      userIds: undefined,
    }));
    setUserAssignmentsChanged(true);
    setClientConfigurations((currentConfigurations) => {
      const currentUserIds = currentConfigurations[activeTargetClient].userIds;
      const bulkUserIdSet = new Set(userIds);
      const nextUserIds = assigned
        ? Array.from(new Set([...currentUserIds, ...userIds]))
        : currentUserIds.filter((currentUserId) => !bulkUserIdSet.has(currentUserId));

      return {
        ...currentConfigurations,
        [activeTargetClient]: {
          ...currentConfigurations[activeTargetClient],
          userIds: nextUserIds,
        },
      };
    });
  }, [
    activeTargetClient,
    setClientConfigurations,
    setFeatureFieldErrors,
    setFeatureFormError,
    setUserAssignmentsChanged,
  ]);

  return {
    handleAssignmentTargetChange,
    handleBulkStoreAssignmentChange,
    handleBulkUserAssignmentChange,
    handleStoreAssignmentChange,
    handleUserAssignmentChange,
  };
};
